import { useMemo } from 'react';
import { ActionIcon, Group, Input, Tooltip } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TablerIcon } from '@tabler/icons-react';
import {
  IconAtom, IconBarbell, IconBook, IconBrain, IconCalculator, IconChartBar, IconCode, IconDna,
  IconFlask, IconHammer, IconHeart, IconHistory, IconLanguage, IconLeaf, IconMap, IconMusic,
  IconPalette, IconShield, IconStar, IconWand, IconWorld,
} from '@tabler/icons-react';
import api from '../services/api';

/**
 * Fan ikonkalari (T4).
 *
 * Kalitlar backenddagi `routes/subjects.ts` → `SUBJECT_ICONS` bilan AYNAN bir xil
 * va TARTIBI ham o'sha (Object.keys satr kalitlar uchun kiritilish tartibini saqlaydi).
 * Backend faqat KALIT saqlaydi/qaytaradi, chizmani esa shu jadval beradi — ya'ni
 * ikonka to'plamini almashtirish uchun DB'ga tegish shart emas.
 *
 * Yangi kalit QO'SHILSA: avval backend whitelistiga, keyin SHU jadvalga. Jadvalda
 * yo'q kalit (eskirgan yozuv yoki backend oldinda ketgan holat) jimgina `book` bilan
 * chiziladi — sahifa yiqilmaydi.
 */
const ICON_MAP: Record<string, TablerIcon> = {
  calculator: IconCalculator,
  atom: IconAtom,
  flask: IconFlask,
  dna: IconDna,
  globe: IconWorld,
  book: IconBook,
  language: IconLanguage,
  code: IconCode,
  palette: IconPalette,
  music: IconMusic,
  dumbbell: IconBarbell,
  hammer: IconHammer,
  brain: IconBrain,
  chart: IconChartBar,
  map: IconMap,
  history: IconHistory,
  shield: IconShield,
  heart: IconHeart,
  leaf: IconLeaf,
  star: IconStar,
};

/** Backend ro'yxati kelmaguncha (yoki so'rov yiqilsa) ishlatiladigan lokal nusxa. */
export const SUBJECT_ICON_KEYS = Object.keys(ICON_MAP);

/** Hech nima mos kelmaganda — backenddagi `FALLBACK_ICON` bilan bir xil. */
const FALLBACK: TablerIcon = IconBook;

/**
 * Ko'rsatiladigan ikonka kontrakti: `icon ?? suggestedIcon`.
 * Taklif (`suggested`) DB'ga YOZILMAYDI — u faqat ko'rinish uchun, admin
 * tanlaganda esa `icon` to'ldiriladi.
 */
export function SubjectIconView({
  icon, suggested, size = 20, color = 'var(--mantine-color-brand-6)',
}: {
  icon?: string | null;
  suggested?: string | null;
  size?: number;
  color?: string;
}) {
  const Cmp = ICON_MAP[icon ?? ''] ?? ICON_MAP[suggested ?? ''] ?? FALLBACK;
  return <Cmp size={size} color={color} />;
}

/**
 * Ruxsat etilgan kalitlar — GET /subjects/icons (backend yagona manba).
 * Chizmasi yo'q kalitlar kesib tashlanadi: aks holda admin bo'sh katakni tanlab,
 * ro'yxatda "ko'rinmas" ikonka bilan qolib ketardi.
 */
export function useSubjectIconKeys(): string[] {
  const { data } = useQuery({
    queryKey: ['subject-icons'],
    queryFn: async () => (await api.get<{ icons: string[] }>('/subjects/icons')).data.icons,
    staleTime: Infinity,
  });
  return useMemo(() => (data ?? SUBJECT_ICON_KEYS).filter((k) => k in ICON_MAP), [data]);
}

/**
 * Ikonka tanlagich — cheklangan ro'yxat (erkin matn EMAS).
 * Birinchi tugma "standart": `value = null`, ya'ni fan nomiga qarab taklif ishlaydi
 * va nom o'zgarsa ikonka ham o'zi moslashadi.
 */
export function SubjectIconPicker({
  value, onChange, suggested, description,
}: {
  value: string | null;
  onChange: (v: string | null) => void;
  suggested?: string | null;
  description?: string;
}) {
  const { t } = useTranslation();
  const keys = useSubjectIconKeys();
  const autoLabel = `${t('subjectsPage.iconAuto')}${suggested ? ` — ${t(`subjectIcon.${suggested}`)}` : ''}`;

  return (
    <Input.Wrapper label={t('subjectsPage.icon')} description={description ?? t('subjectsPage.iconHint')}>
      <Group gap={6} mt={8}>
        <Tooltip label={autoLabel} withArrow>
          <ActionIcon
            variant={value ? 'default' : 'filled'}
            color="brand"
            size="lg"
            aria-label={autoLabel}
            onClick={() => onChange(null)}
          >
            <IconWand size={18} />
          </ActionIcon>
        </Tooltip>
        {keys.map((k) => {
          const Cmp = ICON_MAP[k] ?? FALLBACK;
          const label = t(`subjectIcon.${k}`);
          return (
            <Tooltip key={k} label={label} withArrow>
              <ActionIcon
                variant={value === k ? 'filled' : 'default'}
                color="brand"
                size="lg"
                aria-label={label}
                onClick={() => onChange(k)}
              >
                <Cmp size={18} />
              </ActionIcon>
            </Tooltip>
          );
        })}
      </Group>
    </Input.Wrapper>
  );
}
